import {
  Box,
  Icon,
  SelectedTabProvider,
  Tab,
  Tabs,
} from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import React, { ReactNode, useEffect, useState } from 'react'
import { FulfilmentType } from '../../../models/fulfilment'
import { useVdFeatureManager } from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { selectSaleHasLineItemFulfilment } from '../../../state/slices/currentSale/currentSaleSlice'
import { useSaleHasServiceOrder } from '../../sale/useSaleHasServiceOrder'
import { useLineItemHasFulfillmentAddressError } from './fulfilment/hook/useLineItemHasFulfillmentAddressError'

export enum LineItemTab {
  DETAIL = 'detail',
  FULFILMENT = 'fulfilment',
}

interface LineItemTabsProps {
  defaultTab?: LineItemTab
  onChange: (selected: LineItemTab) => void
  children?: ReactNode | undefined
  fulfillmentType?: FulfilmentType
}

export const LineItemTabs: React.FC<LineItemTabsProps> = ({
  defaultTab = LineItemTab.DETAIL,
  onChange,
  children,
  fulfillmentType,
}) => {
  const t = useT()
  const [currentTab, setCurrentTab] = useState(defaultTab)
  const vdFeatureManager = useVdFeatureManager()
  const isFulfilmentLifEnabled = vdFeatureManager.get('fulfillment_lif')
  const isFulfilmentLifEnabledB2 = vdFeatureManager.get('fulfillment_lif_b3')
  const hasFulfilment = useAppSelector(selectSaleHasLineItemFulfilment)
  const saleHasServiceOrder = useSaleHasServiceOrder()
  const isDisplay = isFulfilmentLifEnabled && hasFulfilment
  const hasAddressErrorForLineItemFulfillmentDelivery =
    useLineItemHasFulfillmentAddressError() &&
    fulfillmentType === FulfilmentType.DELIVERY
  const [displayAddressErrorIcon, setDisplayAddressErrorIcon] = useState(
    hasAddressErrorForLineItemFulfillmentDelivery
  )

  useEffect(() => {
    if (!isDisplay && currentTab === LineItemTab.FULFILMENT) {
      setCurrentTab(LineItemTab.DETAIL)
    }
    setDisplayAddressErrorIcon(
      currentTab !== LineItemTab.FULFILMENT &&
        hasAddressErrorForLineItemFulfillmentDelivery
    )
  }, [isDisplay, currentTab, hasAddressErrorForLineItemFulfillmentDelivery])

  useEffect(() => {
    setCurrentTab(defaultTab)
  }, [defaultTab])

  const displayTabs =
    (isFulfilmentLifEnabled && isFulfilmentLifEnabledB2) || isDisplay

  // Hide the details and fulfilment tabs in sales with service orders, but still show the content for Details
  const shouldShowTabs = displayTabs && !saleHasServiceOrder

  // Reset to Details tab when tabs are hidden (e.g., when service order is added while viewing Fulfillment tab)
  useEffect(() => {
    if (!shouldShowTabs && currentTab !== LineItemTab.DETAIL) {
      setCurrentTab(LineItemTab.DETAIL)
      onChange(LineItemTab.DETAIL)
    }
    // onChange is intentionally omitted from dependencies to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowTabs, currentTab])

  if (!displayTabs) {
    return null
  }

  return (
    <SelectedTabProvider
      defaultTab={defaultTab}
      onChange={string => {
        const selectedTab = string as LineItemTab
        setCurrentTab(selectedTab)
        onChange(selectedTab)
      }}
    >
      {/* When tabs are hidden, thisBox provides the vd-mb3 margin that Tabs would normally provide */}
      <Box className={shouldShowTabs ? undefined : 'vd-mb3'}>
        {shouldShowTabs && (
          <Tabs className="vd-mb3">
            <Tab data-track="details" name={LineItemTab.DETAIL}>
              {t('Details', { _context: 'Line item tabs' })}
            </Tab>
            <Tab data-track="fulfillment" name={LineItemTab.FULFILMENT}>
              {displayAddressErrorIcon && (
                <span data-testid="line-item-icon-error" className="vd-mr2">
                  <Icon
                    icon="exclamation-triangle"
                    className="wr-input-error-icon"
                  />
                </span>
              )}
              {t('Fulfillment', { _context: 'Line item tabs' })}
            </Tab>
          </Tabs>
        )}
      </Box>
      {children}
    </SelectedTabProvider>
  )
}
